export interface AuthenticationResult {
  regularLogin: boolean
  data: AuthenticationData
}

export interface AuthenticationData {
  user: CurrentUser
  currentCustomerId: string | null
  currentCompanyGroupId: string | null
  accessToken: string
}

export interface PaginationData {
  pageIndex: number
  pageSize: number
  totalItemCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export enum Language {
  sv = 'sv',
  en = 'en'
}

export interface CustomerPortalRoleModel {
  id: string
  name: string
  // Permissions
  allowManageUsers: boolean
  allowManagePaySchedules: boolean
  allowManagePayroll: boolean
  allowManageEmployees: boolean
  allowManageShifts: boolean
  allowManagePayDeviations: boolean
  allowManageEmployeeActivations: boolean
  allowManageCompanySettings: boolean
  allowManageBookkeepingSettings: boolean
  allowManageFora: boolean
  allowManageAgi: boolean
  allowViewIntegrationLog: boolean
  allowManageIntegrations: boolean
  // Reports and notifications
  allowReadCreditReport: boolean
  allowReadPaymentNotificationFile: boolean
  receiveAgiAdjustmentReminder: boolean
  receiveCreditReport: boolean
  receiveActivationNotification: boolean
  receivePayrollSystemImportFileNotification: boolean
  receiveDeductionRepaymentNotification: boolean
  receivePayrollFileReminder: boolean
  receivePaydayGenerationNotification: boolean
  receivePayrollFileValidationErrorNotification: boolean
}

export interface CompanyGroupPortalRoleModel {
  id: string
  name: string
  allowManageUsers: boolean
  allowManagePayroll: boolean
  allowManageEmployeeActivations: boolean
}

export interface CreateOrUpdateCustomerPortalRoleModel {
  name: string
  // Permissions
  allowManageUsers: boolean
  allowManagePaySchedules: boolean
  allowManagePayroll: boolean
  allowManageEmployees: boolean
  allowManageShifts: boolean
  allowManagePayDeviations: boolean
  allowManageEmployeeActivations: boolean
  allowManageCompanySettings: boolean
  allowReadCreditReport: boolean
  allowReadPaymentNotificationFile: boolean
  allowManageBookkeepingSettings: boolean
  allowManageFora: boolean
  allowManageAgi: boolean
  allowViewIntegrationLog: boolean
  allowManageIntegrations: boolean
  // Reports and notifications
  receiveAgiAdjustmentReminder: boolean
  receiveCreditReport: boolean
  receiveActivationNotification: boolean
  receivePayrollSystemImportFileNotification: boolean
  receiveDeductionRepaymentNotification: boolean
  receivePayrollFileReminder: boolean
  receivePaydayGenerationNotification: boolean
  receivePayrollFileValidationErrorNotification: boolean
}

export interface CreateOrUpdateCompanyGroupPortalRoleModel {
  name: string
  allowManageUsers: boolean
  allowManagePayroll: boolean
  allowManageEmployeeActivations: boolean
}

export interface CustomerPortalUser {
  portalUserId: string
  identificationNumber: string
  givenName: string
  familyName: string
  email: string
  phoneNumber: string
  language: Language
  customerId: string
  customerName: string
  portalRoles: CustomerPortalRoleModel[]
  isOwner: boolean
  isContactTechnical: boolean
  isContactPayroll: boolean
  isContactFinance: boolean
}

export interface CompanyGroupPortalUser {
  portalUserId: string
  identificationNumber: string
  givenName: string
  familyName: string
  email: string
  phoneNumber: string
  language: Language
  companyGroupId: string
  companyGroupName: string
  portalRoles: CompanyGroupPortalRoleModel[]
  isOwner: boolean
}

export interface CreateCustomerPortalUserModel {
  identificationNumber: string
  email: string
  portalRoleIds: string[]
  isContactTechnical: boolean
  isContactPayroll: boolean
  isContactFinance: boolean
}

export interface CreateCompanyGroupPortalUserModel {
  identificationNumber: string
  email: string
  portalRoleIds: string[]
}

export interface UpdateCustomerPortalUserModel {
  portalRoleIds: string[]
  isContactTechnical: boolean
  isContactPayroll: boolean
  isContactFinance: boolean
}

export interface UpdateCompanyGroupPortalUserModel {
  portalRoleIds: string[]
}

export interface ConnectedCustomer {
  customerId: string
  customerName: string
  portalRoles: CustomerPortalRoleModel[]
  isOwner: boolean
}
export interface ConnectedCompanyGroup {
  id: string
  name: string
  portalRoles: CompanyGroupPortalRoleModel[]
  isOwner: boolean
}

export enum PortalUserAuthenticationScheme {
  Password = 'password',
  Cappy = 'cappy'
}

export interface CurrentUser {
  portalUserId: string
  identificationNumber: string
  givenName: string
  familyName: string
  email: string
  phoneNumber: string
  language: Language
  authScheme: PortalUserAuthenticationScheme
  customers: ConnectedCustomer[]
  companyGroups: ConnectedCompanyGroup[]
}

export interface CurrentUserEditModel {
  givenName: string
  familyName: string
  phoneNumber: string
  language: Language
}

export interface Money {
  amount: number,
  currencyCode: string
}

export interface PayoutSettings {
  minAmountPerPayout: number | undefined | null
  maxAmountPerPayout: number | undefined | null
  maxAmountPerPayday: number | undefined | null
  maxPercentPerPayday: number | undefined | null
  maxNumberOfPayoutsPerPayday: number | undefined | null
  enableCustomShiftSettings: boolean
  maxCustomPercentPerPayday: number | undefined | null
  fee: number
  currency: string
}

export interface EmployeeAndDateRangeFilter {
  employeeId: string | null
  employeeName: string | null
  fromDate: string | null
  toDate: string | null
}

export interface ShiftListFilter {
  employeeId: string | null
  employeeName: string | null
  fromDate: string | null
  toDate: string | null
  status: ShiftStatus | null
  type: ShiftType | null
}

export interface OpenShiftListFilter {
  fromDate: string | null
  toDate: string | null
  includeDeleted: boolean
}

export type CustomerState = 'active' | 'locked' | 'terminated'
export type EmployerState = 'active' | 'locked' | 'terminated'
export type EmployeeActivationType = 'bankAccount' | 'manual' | 'automatic'
export type EmployeeDeactivationType = 'manual' | 'automatic'
export type CalendarMonthPayoutBasisReportGenerationType = 'automatic' | 'manual'
export type PayrollFileFormat = 'none' | 'bankGiro' | 'fortnoxPdf' | 'sus' | 'painV03'
export type EmployeeDataParserType = 'csv' | 'hogia' | 'fram' | 'visma600UpdateOnly' | 'visma600CreateAndUpdate' | 'agdaXlsx'
export type ShiftDataParserType = 'csv' | 'fram'
export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'
export type PayrollSystemImportFileFormat = 'paXml' | 'tlu' | 'hogia'
export type AdminFlowType = 'basisImport' | 'agiAdjustment'
export type PayrollFileEmployeeIdentifierType = "id10" | "externalId" | "custom" | "id12"

export interface CompanyGroup {
  id: string
  name: string
  enableFileAggregation: boolean
  customers: CompanyGroupCustomer[]
}

export interface CompanyGroupCustomer {
  companyId: string
  customerName: string
  includeInFileAggregation: boolean
}

export interface Customer {
  id: string
  name: string
  legalName: string
  companyRegistrationNumber: string
  language: Language
  timeZoneId: string
  state: CustomerState
  invoiceEmailAddress: string
  portalIntegrations: string[]
  defaultPortalUserAuthenticationScheme: PortalUserAuthenticationScheme
  employer: Employer | null
}

export interface Employer {
  id: string
  abbreviation: string
  payoutSettings: PayoutSettings
  creditLimit: Money
  bankAccount: string
  state: EmployerState
  launchDate: string
  employeeActivationType: EmployeeActivationType
  employeeDeactivationType: EmployeeDeactivationType
  calendarMonthPayoutBasisReportGenerationType: CalendarMonthPayoutBasisReportGenerationType
  calenderMonthPayoutBasisReportGenerationDayOfMonth: number | null
  payrollFileFormat: PayrollFileFormat
  enablePayrollFileAdjustment: boolean
  enableManualPayrollFileRowCreation: boolean
  employeeDataParserType: EmployeeDataParserType
  shiftDataParserType: ShiftDataParserType
  activationNotificationDaysOfWeek: DayOfWeek[]
  creditReportDayOfMonth: number
  payrollSystemImportFileFormat: PayrollSystemImportFileFormat
  newEmployeesReportEnabled: boolean
  adminFlowType: AdminFlowType
  defaultTaxTableIdentifier: string
  defaultFixedTaxRatePercent: number | null
  defaultPayoutTaxInPercent: number
  payoutTaxImmutable: boolean
  logos: EmployerLogoModel[]
  payrollFileEmployeeIdentifierType: PayrollFileEmployeeIdentifierType
  manualPayrollFileRowCreationEnabled: boolean
  payrollFileRowDeletionEnabled: boolean
  employerInitiatedPayoutsEnabled: boolean
  openShiftsEnabled: boolean,
  useParallelEmploymentContracts: boolean
}

export interface EmployerLogoModel {
  theme: string
  contentType: string
  image: string
}

export interface CreditUsage {
  totalCreditUsage: Money
  creditLimit: Money
  creditUsagePerPayday: PaydayCreditUsage[]
  dailyCreditUsage: DailyCreditUsage[]
}

export interface PaydayCreditUsage
{
  paydayId: string
  externalPaydayId: string
  paymentDate: string
  creditUsage: Money
}

export interface DailyCreditUsage {
  date: string
  creditUsage: Money
  creditUsageForUpcomingPayday: Money
  creditUsageForFollowingPayday: Money
  creditLimit: Money
}

export enum Periodicity {
  monthly = 'monthly'
}

export enum AccrualType {
  arrears = 'arrears',
  advance = 'advance'
}

export enum PaydayOnWeekendHandling {
  advance = 'advance',
  postpone = 'postpone'
}

export interface PayScheduleModel {
  id: string | null
  identifier: string
  periodicity: Periodicity
  salaryAccrualType: AccrualType
  salaryEarningOnWeekends: boolean
  periodStartDayOfMonth: number
  deviationPeriodEndOffset: number
  defaultPaymentDayOfMonth: number
  paydayOnWeekendHandling: PaydayOnWeekendHandling
  isDefault: boolean
  paydays: PaySchedulePaydayModel[]
}

export interface PaySchedulePaydayModel {
  id: string
  paymentDate: string
  isShared: boolean
  isReadOnly: boolean
  paydayPeriodStartDate: string
  paydayPeriodEndDate: string
  wagesPeriodStartDate: string
  wagesPeriodEndDate: string
  salaryPeriodStartDate: string
  salaryPeriodEndDate: string
  deviationPeriodStartDate: string
  deviationPeriodEndDate: string
}

export interface Payday {
  id: string | null
  externalId: string
  paymentDate: string
  locked: boolean
  completed: boolean
}

export const EmployeeStatus = {
  Registered: 'registered',
  Connected: 'connected',
  Active: 'active',
  Paused: 'paused',
  Terminated: 'terminated'
} as const
export type EmployeeStatus = typeof EmployeeStatus[keyof typeof EmployeeStatus]

export const EmployeeType = {
  Worker: 'worker',
  Salaried: 'salaried'
} as const
export type EmployeeType = typeof EmployeeType[keyof typeof EmployeeType]

export const PayCalculationType = {
  Default: 'default',
  ShiftBasedSalary: 'shiftBasedSalary'
} as const
export type PayCalculationType = typeof PayCalculationType[keyof typeof PayCalculationType]

export const BookkeepingAccountType = {
  SalaryAccount: 'salaryAccount',
  TaxAccount: 'taxAccount',
  CappyAdjustmentAccount: 'cappyAdjustmentAccount',
  SocialFeeAccount: 'socialFeeAccount',
  SocialFeeSettlementAccount: 'socialFeeSettlementAccount'
}
export type BookkeepingAccountType = typeof BookkeepingAccountType[keyof typeof BookkeepingAccountType]

export interface EmployeeTaxSettings {
  taxTableIdentifier: string | null
  fixedTaxRatePercent: number | null
  payoutTaxInPercent: number | null
}
export interface EmployeePayoutSettings {
  maxAmountPerPayday: number | null
  maxPercentagePerPayday: number | null
  maxCustomPercentagePerPayday: number | null
}

export interface EmploymentContract {
  id: string
  externalId: string
  reportId: string
  description: string
  collectiveAgreement: string
  position: string
  payCalculationType: PayCalculationType
  startDateString: string
  endDateString: string
  handleVacationPay: boolean
  vacationPayPercent: number | null
  pays: Pay[]
}
export interface Pay {
  grossHourlyWage: Money | null
  grossSalary: Money | null
  startDateString: string
  endDateString: string | null
}
export interface Employee {
  id: string
  status: EmployeeStatus
  employmentNumber: string | null
  employeeType: EmployeeType
  payCalculationType: PayCalculationType
  externalId: string
  givenName: string
  familyName: string
  identificationNumber: string
  bankAccountNumber: string
  payrollFileIdentifier: string | null
  email: string
  phoneNumber: string
  department: string
  location: string
  costCenter: string
  category: string
  pauseDate: string
  activated: boolean
  activationDate: string
  manuallyCreated: boolean
  dataCollectionConsentFromUser: boolean
  hasUnsettledPayouts: boolean
  taxSettings: EmployeeTaxSettings
  employmentContracts: EmploymentContract[]
  extendTerminationToUpcomingPayday: boolean
  paused: boolean
  pauseReason: string
  nonEligible: boolean
  underForeclosure: boolean
  payoutSettings: EmployeePayoutSettings
  effectivePayoutSettings: EmployeePayoutSettings
  displayPayoutLimitationInfo: boolean
  payoutLimitationReason: string | null
  internalComment: string | null
}

export type EditEmployeeModel = Pick<
  Employee,
  | 'extendTerminationToUpcomingPayday'
  | 'paused'
  | 'pauseReason'
  | 'nonEligible'
  | 'underForeclosure'
  | 'payoutSettings'
  | 'displayPayoutLimitationInfo'
  | 'payoutLimitationReason'
  | 'internalComment'
>

export const EmployeeFlagType = {
  UnderForeclosure: 'underForeclosure',
  PayoutLimitation: 'payoutLimitation',
  UnsettledPayouts: 'unsettledPayouts',
  NonEligible: 'nonEligible',
} as const
export type EmployeeFlagType = typeof EmployeeFlagType[keyof typeof EmployeeFlagType]

export interface EmployeeFilter {
  term: string | undefined,
  departments: string[],
  categories: string[],
  statuses: EmployeeStatus[],
  flags: EmployeeFlagType[]
}

export interface EmployeeMinModel {
  id: string
  externalId: string
  name: string
  identificationNumber: string
}

export interface NewEmployeeInPeriodModel {
  id: string
  externalId: string
  givenName: string
  familyName: string
  identificationNumber: string
}

export interface NewEmployeesWithPayoutsInPeriodModel {
  startDate: string | undefined
  endDate: string | undefined
  employees: NewEmployeeInPeriodModel[] | undefined
}

export interface PaydayMinModel {
  id: string
  paymentDate: string
}

export const ShiftType = {
  Standard: 'standard',
  Custom: 'custom'
} as const
export type ShiftType = typeof ShiftType[keyof typeof ShiftType]

export const ShiftStatus = {
  Completed: 'completed',
  Planned: 'planned'
} as const
export type ShiftStatus = typeof ShiftStatus[keyof typeof ShiftStatus]

export interface Shift {
  id: string
  externalId: string
  date: string
  type: ShiftType
  status: ShiftStatus
  hours: number
  startTime: string | null
  endTime: string | null
  grossValue: Money
  manuallyCreated: boolean
  employee: EmployeeMinModel
  location: string
  description: string,
  payArticles: PayArticleModel[]
}

export interface PayArticleModel {
  code: string
  displayName: string
  isShiftBasedSalaryArticle: boolean
  grossValue: Money
  quantity: number
  payday: PaydayMinModel
  accrualType: AccrualType
  approved: boolean
}

export interface PagedShifts {
  shifts: Shift[]
  totalShiftCount: number
}

export interface OpenShift {
  id: string
  externalId: string
  date: string
  applicantCount: number
  hours: number
  startTime: string
  endTime: string
  grossWages: Money
  location1: string
  location2: string
  location3: string
  comment: string
  externalUrl: string
  deleted: boolean
  notificationsSent: boolean
  summaryNotificationSent: boolean
  requirements: string[]
}

export interface PagedOpenShifts {
  shifts: OpenShift[]
  totalShiftCount: number
}

export interface OpenShiftRecipient {
  id: string
  externalId: string
  employmentNumber: string
  name: string
  openShiftIsNew: boolean
  scheduleConflict: boolean
  restingTimeRuleViolation: boolean
}

export interface OpenShiftRecipients {
  openShiftId: string
  openShiftRecipients: OpenShiftRecipient[]
}

export interface PayDeviation {
  id: string
  externalId: string
  description: string
  date: string
  grossValue: Money
  manuallyCreated: boolean
  employee: EmployeeMinModel
  payArticles: PayArticleModel[]
}

export interface EditPayDeviationModel {
  externalId: string
  date: string
  externalEmployeeId: string
  externalEmploymentContractId: string | null
  description: string
  grossValue: number
}

export interface PagedPayDeviations {
  deviations: PayDeviation[]
  totalCount: number
}

export interface EmployeeReadyForActivation {
  id: string
  externalId: string
  givenName: string
  familyName: string
  identificationNumber: string
  department: string
  requestDate: string
  pendingBankAccountActivation: boolean
  bankAccountActivationFailed: boolean
}

export interface EmployeeReadyForDeactivation {
  id: string
  externalId: string
  givenName: string
  familyName: string
  identificationNumber: string
  department: string
}

export interface DeactivatedEmployee {
  id: string
  employeeId: string
  externalId: string
  employmentNumber: string | null
  givenName: string
  familyName: string
  identificationNumber: string
  deactivationDate: string
  deactivatedByPortalUserName: string | null
}

export const IntegrationLogLevel = {
  Information: 'information',
  Error: 'error'
} as const
export type IntegrationLogLevel = typeof IntegrationLogLevel[keyof typeof IntegrationLogLevel]


export interface IntegrationLogItem {
  date: string
  level: IntegrationLogLevel
  type: string
  txId: string
  parameters: string
}

export interface PagedIntegrationLogItems {
  items: IntegrationLogItem[]
  totalCount: number
}

export interface IntegrationLogFilter {
  level: IntegrationLogLevel | null
  txId: string | null
  fromDate: string | null
  toDate: string | null
}


export type PayrollStatus = 'running' | 'completed'
export interface Payroll {
  paydayId: string
  paydayDate: string
  status: PayrollStatus
  deliveredPays: number
  remainingPaysToDeliver: number
  startedDate: string
  completedDate: string
  offCycle: boolean
}

export type PayrollValidationErrorType = 'unknownEmployeeInPayroll' | 'employeeMissingInPayroll' | 'employeeWithNegativeBalance' | 'nonActivatedEmployeeInPayroll' | 'employeeWithIncorrectBankAccount' | 'employeeWithIdentificationNumberMismatch' | 'paymentDateDifferentFromPayday' | 'possibleDuplication'
export interface PayrollValidationError {
  type: PayrollValidationErrorType
  payrollFileRowId: string | undefined
  parameters: string
}

export interface PayrollFile {
  externalId: string
  submitDate: string
  payrollFileRows: PayrollFileRow[]
}

export interface PayrollFileRow {
  id: string
  employeeId: string
  externalEmployeeId: string
  employeeName: string
  employeeIdentificationNumber: string
  netAmount: Money
}

export interface UploadPayrollFileRowsModel {
  originalRowCount: number
  rows: UploadPayrollFileRowModel[]
}

export interface UploadPayrollFileRowModel {
  employeePayrollFileIdentifier: string
  identificationNumber: string
  text: string
  amount: number
  bankAccountNumber: string
}

export interface UpcomingPayroll {
  paydayId: string
  paymentDate: string
  offCycle: boolean
  latestPayrollFileSubmitDate: string
  earliestPayrollCreationDate: string | undefined
  payrollCreated: boolean
  payrollCreateDate: string
  payrollFileDue: boolean
  payrollFileOverdue: boolean
  payrollFileSubmitted: boolean
  payrollFiles: PayrollFile[]
  validationErrors: PayrollValidationError[]
}

export enum EmployerInitiatedPayoutJobState {
  scheduled = "scheduled",
  cancelled = "cancelled",
  pending = "pending",
  completed = "completed"
}

export enum EmployerInitiatedPayoutBasisState {
  scheduled = "scheduled",
  cancelled = "cancelled",
  pending = "pending",
  unprocessable = "unprocessable",
  failed = "failed",
  completed = "completed"
}

export interface EmployerInitiatedPayoutJobListItem {
  id: string
  scheduledDate: string
  completedDate: string | null
  state: EmployerInitiatedPayoutJobState
  deliveredPaysCount: number
  remainingPaysCount: number
}

export interface EmployerInitiatedPayoutJob {
  id: string
  scheduledDate: string
  completedDate: string | null
  state: EmployerInitiatedPayoutJobState
  paydayId: string
  basis: EmployerInitiatedPayoutBasis[]
}

export interface EmployerInitiatedPayoutBasis {
  id: string
  employee: EmployerInitiatedPayoutEmployeeModel
  grossAmount: Money
  netAmount: Money
  taxPercent: number
  state: EmployerInitiatedPayoutBasisState
}

export interface EmployerInitiatedPayoutEmployeeModel {
  id: string
  externalId: string
  identificationNumber: string
  givenName: string
  familyName: string
  status: EmployeeStatus
}

export interface PayrollData {
  payrolls: Payroll[]
  upcomingPayrolls: UpcomingPayroll[]
}

export type PayDeliveryStatus = 'pending' | 'completed'
export interface PayRun {
  id: string
  name: string
  identificationNumber: string
  externalEmployeeId: string
  employmentNumber: string
  originalPay: Money
  payDeliveryStatus: PayDeliveryStatus
  needsPaymentMethodUpdate: boolean
  subjectToDeductions: boolean
}

export interface DashboardMetrics {
  employeeCount: number
  employeeCountAtStartOfMonth: number
  activatedEmployeeCount: number
  activatedEmployeeCountAtStartOfMonth: number
  loginCountCurrentMonth: number
  totalLoginCount: number
  payoutCountCurrentMonth: number
  totalPayoutCount: number
  payoutSumCurrentMonth: Money
  totalPayoutSum: Money
}

export enum FileType {
  advancePayoutBasisFile = 'advancePayoutBasisFile',
  deductionPayoutBasisFile = 'deductionPayoutBasisFile',
  payDeductionNotificationFile = 'payDeductionNotificationFile',
  bankAccountFile = 'bankAccountFile',
  creditReportFile = 'creditReportFile',
  agiAdjustmentSieFile = 'agiAdjustmentSieFile',
  agiAdjustmentReportFile = 'agiAdjustmentReportFile',
  foraAdjustmentReportFile = 'foraAdjustmentReportFile',
  remainingDebtReportFile = 'remainingDebtReportFile',
  employeeForeclosureReportFile = 'employeeForeclosureReportFile',
  grossAdvanceReportFile = 'grossAdvanceReportFile',
  manualSendBackPaysReportFile = 'manualSendBackPaysReportFile',
  employeeDeactivationFile = 'employeeDeactivationFile',
}
export interface FileModel {
  id: string
  type: FileType
  advancePayoutBasisFile: AdvancePayoutBasisFileModel
  deductionPayoutBasisFile: DeductionPayoutBasisFileModel
  employerPayDeductionPaymentNotificationFile: PayDeductionPaymentFileModel
  bankAccountFile: BankAccountImportFileModel
  creditReportFile : CreditReportFileModel
  agiAdjustmentSieFile: AgiAdjustmentSieFileModel
  agiAdjustmentReportFile: AgiAdjustmentReportFileModel,
  foraAdjustmentReportFile: ForaAdjustmentReportFileModel,
  remainingDebtReportFile: RemainingDebtReportFileModel,
  employeeForeclosureReportFile: EmployeeForeclosureReportFileModel,
  grossAdvanceReportFile: GrossAdvanceReportFileModel,
  manualSendBackPaysReportFile: ManualSendBackPaysReportFileModel,
  employeeDeactivationFile: EmployeeDeactivationImportFileModel
}
export interface EmployerInfoModel {
  employerId: string
  name: string
}
export interface AdvancePayoutBasisFileModel {
  year: number
  month: number
  mimeType: string
  filename: string
  includedEmployers: EmployerInfoModel[]
}
export interface DeductionPayoutBasisFileModel {
  paydayPaymentDate: string
  mimeType: string
  filename: string
  includedEmployers: EmployerInfoModel[]
}
export interface PayDeductionPaymentFileModel {
  paydayId: string
  paydayPaymentDate: string
  mimeType: string
  filename: string
  lastDownloadDate: string | null
}
export interface BankAccountImportFileModel {
  mimeType: string
  filename: string
  includedEmployers: EmployerInfoModel[]
}
export interface EmployeeDeactivationImportFileModel {
  mimeType: string
  filename: string
  includedEmployers: EmployerInfoModel[]
}
export interface CreditReportFileModel {
  year: number
  month: number
  mimeType: string
  filename: string
  lastDownloadDate: string | null
}
export interface AgiAdjustmentSieFileModel {
  period: string
  mimeType: string
  filename: string
  lastDownloadDate: string | null
}

export interface AgiAdjustmentReportFileModel {
  period: string
  mimeType: string
  filename: string
}

export interface ForaAdjustmentReportFileModel {
  period: string
  mimeType: string
  filename: string
}

export interface RemainingDebtReportFileModel {
  paydayId: string
  payday: string
  filename: string
  mimeType: string
}

export interface EmployeeForeclosureReportFileModel {
  paydayId: string
  payday: string
  filename: string
  mimeType: string
}
export interface ManualSendBackPaysReportFileModel {
  paydayId: string
  payday: string
  filename: string
  mimeType: string
}
export interface GrossAdvanceReportFileModel {
  year: number
  month: number
  mimeType: string
  filename: string
}

export interface PayoutBasisYearAndMonthModel {
  year: number | null
  month: number | null
}
export interface PayoutBasisGenerationResult {
  generatedImportFileCount: number
}

export interface BulkOperationResult {
  processCount: number
  insertCount: number
  updateCount: number
  errors: string[]
}

export interface AgiAdjustmentsModel {
  currentPeriod: string
  isCurrentPeriodAdjusted: boolean
  adjustedPeriods: AgiAdjustmentPeriodSummary[]
}

export interface AgiAdjustmentPeriodSummary {
  period: string
  adjustmentCount: number
}

export interface AgiAdjustmentsForPeriod {
  period: string
  adjustments: AgiAdjustment[]
  sieFile: FileModel
  agiAdjustmentReportFile: FileModel
}

export interface ForaAdjustmentsModel {
  currentPeriod: string
  isCurrentPeriodAdjusted: boolean
  adjustedPeriods: ForaAdjustmentPeriodSummary[]
}

export interface ForaAdjustmentPeriodSummary {
  period: string
  adjustmentCount: number
}

export interface ForaAdjustmentsForPeriod {
  period: string
  adjustments: ForaAdjustment[]
  foraAdjustmentReportFile: FileModel
}

export interface ForaAdjustment {
  employeeId: string
  externalId: string
  employeeName: string
  identificationNumber: string
  originalGrossPay: number
  grossPayAdditions: number
  grossPayDeductions: number
}

export enum SwedishTaxType {
  default = 'Default',
  aSINK = 'ASINK',
  sINK = 'SINK',
  none = 'None'
}
export enum AgiAdjustmentError {
  ejFastDriftstalleIndividUnsupported = 'EjFastDriftstalleIndividUnsupported',
  konventionMedUnsupported = 'KonventionMedUnsupported',
  payLessThanDeduction = 'PayLessThanDeduction'
}

export interface AgiAdjustment {
  employeeId: string
  externalId: string
  employeeName: string
  identificationNumber: string
  addedByCappy: boolean
  workplaceStreetAddress: string
  workplaceCity: string
  originalGrossPay: number
  grossPayAdditions: number
  grossPayDeductions: number
  originalTax: number
  taxAdditions: number
  taxDeductions: number
  taxType: SwedishTaxType
  socialFeeAdjustment: number
  error?: AgiAdjustmentError
}

export interface BookkeepingConfiguration {
  verificationDescription: string | undefined,
  verificationsPerIndividual: boolean | undefined,
  accountTypeConfigurations: BookkeepingAccountTypeConfiguration[]
}

export interface BookkeepingAccountTypeConfiguration {
  accountType: BookkeepingAccountType
  includeCostCenterDimension: boolean | undefined,
  includeEmploymentDimension: boolean | undefined,
  includeProjectDimension: boolean | undefined
  rules: BookkeepingAccountTypeRule[]
}

export interface BookkeepingAccountTypeRule {
  orderNumber: number
  account: number
  costCenter: string | null
  project: string | null
  filterEmployeeType: EmployeeType | null
  filterDepartment: string | null
  filterCategory: string | null
}
